import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardMedia, Grid, Box, Button, Alert, TextField, Snackbar } from '@mui/material';

const DramaBox = () => {
  const [books, setBooks] = useState([]);
  const [searchBooks, setSearchBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [lastWatchSession, setLastWatchSession] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [page, setPage] = useState(2);
  const [isFetching, setIsFetching] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/genres.json')
      .then((response) => response.json())
      .then((data) => setCategories(data.pageProps?.types || []))
      .catch((error) => console.error('Error fetching categories:', error));
  }, []);

  useEffect(() => {
    const storedCategory = localStorage.getItem('selectedCategory');
    if (storedCategory && storedCategory !== selectedCategory) {
      setSelectedCategory(storedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    const fetchBooks = () => {
      let url = '';
      if (selectedCategory) {
        url = `https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/genres/${selectedCategory}.json`;
      } else if (!debouncedSearchValue) {
        url = `https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/genres.json`;
      } else {
        return;
      }

      fetch(url)
        .then((response) => response.json())
        .then((data) => setBooks(data.pageProps?.bookList || []))
        .catch((error) => console.error('Error fetching books:', error));
    };

    if (selectedCategory || debouncedSearchValue) {
      fetchBooks();
    }
  }, [selectedCategory, debouncedSearchValue]);

  useEffect(() => {
    if (debouncedSearchValue) {
      const searchUrl = `https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/search.json?searchValue=${debouncedSearchValue}`;
      fetch(searchUrl)
        .then((response) => response.json())
        .then((data) => setSearchBooks(data.pageProps?.bookList || []))
        .catch((error) => console.error('Error fetching search results:', error));
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (!isFetching) return;

    const fetchMoreBooks = () => {
      let url = '';
      if (selectedCategory) {
        url = `https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/genres/${selectedCategory}/${page}.json`;
      } else {
        url = `https://www.dramaboxdb.com/_next/data/dramaboxdb_prod_20250114/in/genres/0/${page}.json`;
      }

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          const newBooks = data.pageProps?.bookList || [];
          if (newBooks.length > 0) {
            setBooks((prevBooks) => [...prevBooks, ...newBooks]);
            setPage((prevPage) => prevPage + 1);
          } else {
            setAlertMessage('Data sudah habis');
            setIsAlertOpen(true);
          }
          setIsFetching(false);
        })
        .catch((error) => console.error('Error fetching more books:', error))
        .finally(() => setIsFetching(false));
    };

    setAlertMessage('Loading...');
    setIsAlertOpen(true);
    fetchMoreBooks();
  }, [isFetching, page, selectedCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 100 &&
        !isFetching &&
        !debouncedSearchValue
      ) {
        setIsFetching(true);
      }
    };

    let timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(handleScroll, 200);
    };

    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [isFetching, debouncedSearchValue]);

  useEffect(() => {
    const session = localStorage.getItem('lastWatchSession');
    if (session) {
      setLastWatchSession(JSON.parse(session));
    }
  }, []);

  const handleContinueWatching = () => {
    if (lastWatchSession) {
      const { bookId, bookNameLower, currentVideoIndex } = lastWatchSession;
      navigate(`/dramabox/${bookId}/${bookNameLower}?chapter=${currentVideoIndex}`);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    localStorage.setItem('selectedCategory', categoryId);
    setSearchValue('');
    setPage(2);
    setBooks([]);
  };

  return (
    <Box sx={{ padding: 2 }}>
      {lastWatchSession && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleContinueWatching}>
              Lanjutkan
            </Button>
          }
          sx={{ marginBottom: 2 }}
        >
          Anda terakhir menonton <strong>{lastWatchSession.bookName}</strong>.
        </Alert>
      )}

      <Box sx={{ marginBottom: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={String(selectedCategory) === String(category.id) ? 'contained' : 'outlined'}
            onClick={() => handleCategoryChange(category.id)}
            sx={{
              flex: '1 1 auto',
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            {category.name}
          </Button>
        ))}
      </Box>

      <TextField
        label="Cari Drama"
        variant="outlined"
        fullWidth
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      <Grid container spacing={2}>
        {(debouncedSearchValue ? searchBooks : books).map((book, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Card
              sx={{
                position: 'relative',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
              component={Link}
              to={`/dramabox/${book.bookId}/${book.bookNameLower}`}
            >
              <CardMedia
                component="img"
                sx={{
                  height: '100%',
                  objectFit: 'fill',
                }}
                image={debouncedSearchValue ? book.coverWap : book.cover}
                alt={book.bookName}
              />

              {/* Bagian atas */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  fontSize: '0.8rem',
                  borderRadius: '4px',
                }}
              >
                Episode: {debouncedSearchValue ? book.totalChapterNum : book.chapterCount}
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 'auto', sm: 0 },
                  bottom: { xs: 0, sm: 'auto' },
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  fontSize: '0.8rem',
                  borderRadius: '4px',
                }}
              >
                {!debouncedSearchValue
                  ? book.typeTwoNames?.[0]
                  : book.bookTypeTwo ? Object.values(book.bookTypeTwo)[0] : 'Kategori tidak tersedia'}
              </Box>

              {/* Judul Buku */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRadius: '0 0 4px 4px',
                }}
              >
                {book.bookName}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={isAlertOpen}
        autoHideDuration={1500}
        onClose={() => setIsAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setIsAlertOpen(false)} severity="info" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DramaBox;